/**
 * Business Suite Design Tokens
 * Centralized design system constants for consistent styling across all business features
 * Based on the Communication Dashboard modern design implementation (December 2024)
 */

// =============================================================================
// COLOR SYSTEM
// =============================================================================

export const colors = {
  // Primary Actions & Branding
  primary: {
    // Brand forest green (#2C5F2D) via the --primary theme token
    main: "primary",
    light: "primary/80",
    bg: "primary/10",
    text: "primary",
    border: "primary/20",
    hover: "primary/90",
  },

  // Success States & Positive Actions
  success: {
    main: "emerald-600",
    light: "emerald-500",
    bg: "emerald-50",
    text: "emerald-700",
    border: "emerald-200/80",
    hover: "emerald-700",
  },

  // Accent & Premium Features
  accent: {
    main: "purple-600",
    light: "purple-500",
    bg: "purple-50",
    text: "purple-700",
    border: "purple-200/80",
    hover: "purple-700",
  },

  // Warnings & Important Information
  warning: {
    main: "amber-600",
    light: "amber-500",
    bg: "amber-50",
    text: "amber-700",
    border: "amber-200/50",
    hover: "amber-700",
  },

  // Neutral & Text Colors
  neutral: {
    900: "slate-900",
    800: "slate-800",
    700: "slate-700",
    600: "slate-600",
    500: "slate-500",
    400: "slate-400",
    300: "slate-300",
    200: "slate-200",
    100: "slate-100",
    50: "slate-50",
  },

  // Background Colors
  backgrounds: {
    layout: "#F0F4F0",           // Main BusinessLayout background
    card: "white",               // Card backgrounds
    section: "slate-50/50",      // Subtle section backgrounds
    header: "slate-50/80",       // Card header backgrounds
  },
} as const;

// =============================================================================
// SHADOW SYSTEM
// =============================================================================

export const shadows = {
  // Level 1: Basic Cards & Elements
  level1: {
    default: "shadow-md shadow-slate-900/5",
    hover: "shadow-lg shadow-slate-900/8",
    className: "shadow-md shadow-slate-900/5 hover:shadow-lg hover:shadow-slate-900/8 transition-shadow duration-200"
  },

  // Level 2: Important Sections & Modals
  level2: {
    default: "shadow-lg shadow-slate-900/8",
    hover: "shadow-xl shadow-slate-900/12",
    className: "shadow-lg shadow-slate-900/8 hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300"
  },

  // Colored Shadows for Gradient Elements
  colored: {
    emerald: "shadow-lg shadow-emerald-500/25",
    blue: "shadow-lg shadow-blue-500/25",
    purple: "shadow-md shadow-purple-500/20",
    amber: "shadow-sm shadow-amber-500/25",
  },

  // Simple Shadows
  simple: {
    sm: "shadow-sm",
    md: "shadow-md",
    lg: "shadow-lg",
    xl: "shadow-xl",
  }
} as const;

// =============================================================================
// BORDER SYSTEM
// =============================================================================

export const borders = {
  // Container Borders
  container: "border-slate-200/60",
  interactive: "border-slate-200/80",
  inner: "border-slate-100/80",
  
  // Semantic Borders
  success: "border-emerald-200/50",
  primary: "border-primary/20",
  accent: "border-purple-200/80",
  warning: "border-amber-200/50",
  
  // Border Radius
  radius: {
    sm: "rounded-sm",
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    full: "rounded-full",
  },

  // Focus States
  focus: {
    primary: "focus:ring-primary focus:border-primary focus:ring-2 focus:ring-offset-2",
    success: "focus:ring-emerald-500 focus:border-emerald-500 focus:ring-2 focus:ring-offset-2",
    warning: "focus:ring-amber-500 focus:border-amber-500 focus:ring-2 focus:ring-offset-2",
  }
} as const;

// =============================================================================
// TYPOGRAPHY SYSTEM
// =============================================================================

export const typography = {
  // Headers
  h1: "text-3xl font-bold tracking-tight",
  h2: "text-2xl font-semibold",
  h3: "text-xl font-semibold",
  h4: "text-lg font-semibold",
  h5: "text-base font-semibold",
  
  // Body Text
  body: "text-sm",
  bodyLarge: "text-base",
  caption: "text-xs",
  
  // Special Effects
  gradientText: "bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent",
  
  // Color Classes
  primary: "text-slate-900",
  secondary: "text-slate-600", 
  muted: "text-slate-400",
  
  // Weights
  weights: {
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold",
    bold: "font-bold",
  }
} as const;

// =============================================================================
// SPACING SYSTEM
// =============================================================================

export const spacing = {
  // Main Layout Spacing
  sectionGap: "space-y-8",
  cardGap: "space-y-6",
  elementGap: "space-y-4",
  
  // Grid Systems
  mainGrid: "gap-8",
  cardGrid: "gap-6",
  elementGrid: "gap-4",
  smallGrid: "gap-3",
  
  // Padding
  cardPadding: "p-6",
  sectionPadding: "p-4",
  elementPadding: "p-3",
  smallPadding: "p-2",
  
  // Margins
  headerMargin: "mb-6",
  sectionMargin: "mb-8",
  elementMargin: "mb-4",
} as const;

// =============================================================================
// ICON SYSTEM
// =============================================================================

export const icons = {
  // Icon Sizes
  sizes: {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
    xl: "w-8 h-8",
  },
  
  // Icon Containers
  containers: {
    small: "w-8 h-8 rounded-lg",
    medium: "w-10 h-10 rounded-xl",
    large: "w-12 h-12 rounded-xl",
    huge: "w-16 h-16 rounded-xl",
  },
  
  // Container Styles
  styles: {
    primary: "bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25",
    success: "bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-500/25",
    accent: "bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg shadow-purple-500/25",
    warning: "bg-gradient-to-br from-amber-100 to-amber-200 border border-amber-200/50 shadow-sm",
    neutral: "bg-slate-100 border border-slate-200/50 shadow-sm",
  }
} as const;

// =============================================================================
// TRANSITION SYSTEM
// =============================================================================

export const transitions = {
  // Smooth Transitions
  shadow: "transition-shadow duration-200",
  shadowSlow: "transition-shadow duration-300",
  all: "transition-all duration-200",
  colors: "transition-colors duration-200",
  
  // Hover Effects
  hover: {
    shadowLift: "hover:shadow-lg hover:shadow-slate-900/8",
    shadowLiftStrong: "hover:shadow-xl hover:shadow-slate-900/12",
    borderHighlight: "hover:border-slate-300",
  }
} as const;

// =============================================================================
// COMPONENT PATTERNS
// =============================================================================

export const components = {
  // Card Patterns
  card: {
    basic: "border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white hover:shadow-lg hover:shadow-slate-900/8 transition-shadow duration-200",
    premium: "border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300",
    header: "pb-4 border-b border-slate-100/80",
    headerPremium: "bg-slate-50/80 border-b border-slate-200/50",
    content: "space-y-5 pt-6",
  },

  // Button Patterns
  button: {
    primary: "bg-primary hover:bg-primary/90 px-6 shadow-md hover:shadow-lg transition-shadow",
    secondary: "border-slate-200/80 shadow-sm hover:shadow-md transition-shadow",
    success: "bg-emerald-600 hover:bg-emerald-700 shadow-md hover:shadow-lg transition-shadow",
  },

  // Badge Patterns
  badge: {
    success: "bg-emerald-100 text-emerald-800 border border-emerald-200/50 shadow-sm",
    primary: "bg-blue-100 text-blue-800 border border-blue-200/50 shadow-sm",
    warning: "bg-orange-100 text-orange-800 border border-orange-200/50 shadow-sm",
    outline: "border-slate-200/80 shadow-sm",
  },

  // Form Elements
  form: {
    input: "focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm",
    textarea: "focus:ring-primary focus:border-primary resize-none border-slate-200/80 shadow-sm",
    select: "focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm",
  },

  // Status Pills
  statusPill: {
    success: "flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200/80 shadow-sm",
    primary: "flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200/80 shadow-sm",
    accent: "flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200/80 shadow-sm",
  },
} as const;

// =============================================================================
// LAYOUT PATTERNS
// =============================================================================

export const layouts = {
  // Grid Systems
  mainGrid: "grid grid-cols-1 lg:grid-cols-2 gap-8",
  statsGrid: "grid grid-cols-1 md:grid-cols-4 gap-6",
  threeColumnGrid: "grid grid-cols-1 md:grid-cols-3 gap-4",
  
  // Section Patterns
  pageContainer: "space-y-8",
  contentContainer: "space-y-8",
  headerContainer: "relative",
  
  // Flex Patterns
  headerFlex: "flex items-center gap-3",
  pillFlex: "flex items-center gap-4 pt-2",
  cardHeaderFlex: "flex items-center gap-3",
} as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get semantic color classes for a given state
 */
export const getSemanticColors = (variant: 'primary' | 'success' | 'warning' | 'accent') => {
  return {
    background: colors[variant].bg,
    text: colors[variant].text,
    border: colors[variant].border,
    main: colors[variant].main,
    hover: colors[variant].hover,
  };
};

/**
 * Get icon container classes for a given style and size
 */
export const getIconContainer = (
  style: keyof typeof icons.styles,
  size: keyof typeof icons.containers = 'large'
) => {
  return `${icons.containers[size]} ${icons.styles[style]} flex items-center justify-center`;
};

/**
 * Get card classes for a given level
 */
export const getCardClasses = (level: 'basic' | 'premium' = 'basic') => {
  return components.card[level];
};

/**
 * Get shadow classes for a given level
 */
export const getShadowClasses = (level: 'level1' | 'level2' = 'level1') => {
  return shadows[level].className;
};

/**
 * Get button classes for a given variant
 */
export const getButtonClasses = (variant: 'primary' | 'secondary' | 'success' = 'primary') => {
  return components.button[variant];
};

// =============================================================================
// GRADIENT DEFINITIONS (CSS-in-JS format)
// =============================================================================

export const gradients = {
  primary: "linear-gradient(135deg, rgb(59 130 246) 0%, rgb(37 99 235) 100%)",
  success: "linear-gradient(135deg, rgb(16 185 129) 0%, rgb(5 150 105) 100%)",
  accent: "linear-gradient(135deg, rgb(168 85 247) 0%, rgb(147 51 234) 100%)",
  text: "linear-gradient(135deg, rgb(15 23 42) 0%, rgb(51 65 85) 50%, rgb(71 85 105) 100%)",
} as const;

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type ColorVariant = 'primary' | 'success' | 'warning' | 'accent';
export type ShadowLevel = 'level1' | 'level2';
export type IconStyle = keyof typeof icons.styles;
export type IconSize = keyof typeof icons.sizes;
export type IconContainer = keyof typeof icons.containers;
export type CardLevel = 'basic' | 'premium';
export type ButtonVariant = 'primary' | 'secondary' | 'success';

// =============================================================================
// ACCESSIBILITY CONSTANTS
// =============================================================================

export const accessibility = {
  // WCAG Compliant Focus Rings
  focusRing: "focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-none",
  focusRingSuccess: "focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:outline-none",
  
  // Minimum Touch Targets (44px)
  minTouchTarget: "min-h-[44px] min-w-[44px]",
  
  // Screen Reader Only
  srOnly: "sr-only",
  
  // High Contrast Support
  highContrast: "contrast-more:border-slate-800 contrast-more:text-slate-900",
} as const;

// Unified Tab System
export const tabSystem = {
  // Container Styling
  container: {
    base: "space-y-6",
    list: "bg-slate-50/80 p-1 rounded-lg shadow-sm border border-slate-200/60 h-12",
    listWrapper: "border-b border-slate-200/60"
  },
  
  // Tab Trigger Styling
  trigger: {
    base: "flex items-center gap-2 transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10",
    inactive: "text-slate-600 hover:text-slate-900 hover:bg-white/50",
    active: "data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm",
    complete: "flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
  },
  
  // Icon Guidelines
  icon: {
    size: "w-4 h-4",
    spacing: "mr-2"
  },
  
  // Content Styling
  content: {
    base: "space-y-6",
    padding: "p-6"
  },
  
  // Badge Styling (for tab counts)
  badge: {
    base: "ml-1 text-xs bg-white/20 text-current border-0",
    variant: "secondary"
  },
  
  // Sizing Standards
  sizing: {
    listHeight: "h-12",
    triggerHeight: "h-10",
    triggerPadding: "px-4 py-2",
    iconSize: "w-4 h-4",
    fontSize: "text-sm",
    fontWeight: "font-medium"
  }
} as const;

// Standard Tab Icons Mapping
export const standardTabIcons = {
  // Core Business Functions
  dashboard: "BarChart3",
  inventory: "Package", 
  activities: "Calendar",
  bookings: "CalendarCheck",
  calendar: "Calendar",
  
  // Management Functions
  settings: "Settings",
  staff: "Users",
  communication: "MessageSquare",
  analytics: "TrendingUp",
  
  // Specialized Functions
  health: "Stethoscope",
  breeding: "Baby",
  tasks: "ClipboardList",
  conservation: "Shield",
  rehabilitation: "Activity",
  
  // Technical Functions
  mapping: "Map",
  funding: "DollarSign",
  livestock: "PawPrint",
  security: "Lock",
  notifications: "Bell",
  
  // Content Management
  profile: "User",
  features: "Zap",
  subscription: "CreditCard"
} as const;

// Tab Configuration Helper
export const createTabConfig = (
  tabs: Array<{
    value: string;
    label: string;
    icon: keyof typeof standardTabIcons;
    badge?: string | number;
  }>
) => {
  return tabs.map(tab => ({
    ...tab,
    iconName: standardTabIcons[tab.icon]
  }));
};

export default {
  colors,
  shadows,
  borders,
  typography,
  spacing,
  icons,
  transitions,
  components,
  layouts,
  gradients,
  accessibility,
  // Helper functions
  getSemanticColors,
  getIconContainer,
  getCardClasses,
  getShadowClasses,
  getButtonClasses,
  tabSystem,
  standardTabIcons,
  createTabConfig,
}; 